import { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, Plus, Sliders } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Separator } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  INFLOW_SOURCES,
  OUTFLOW_SOURCES,
  SOURCE_COLOURS,
  SOURCE_LABELS,
  useForecast,
  useForecastAccuracy,
  useSaveScenario,
  useScenarios,
  type FlowSource,
  type ForecastScenario,
} from './api';

const ALL_SOURCES: FlowSource[] = [...INFLOW_SOURCES, ...OUTFLOW_SOURCES];

/** Minor units to a plain number, for the chart's axis. */
const toNumber = (value: string | null | undefined) => (value ? Number(value) : 0);

/**
 * The rolling cash-flow forecast (spec §11.6).
 *
 * The point of the page is not the line — it is the toggles beside it. Each
 * source can be switched off, and the balance redraws without it, so the
 * question "what happens if the pipeline does not land?" is answered by
 * unticking one box rather than by arguing about a single blended number.
 */
export function ForecastPage() {
  const [scenarioId, setScenarioId] = useState<string>('');
  const [active, setActive] = useState<FlowSource[]>(ALL_SOURCES);
  const [editing, setEditing] = useState<ForecastScenario | 'new' | null>(null);

  const { data: scenarios } = useScenarios();
  const { data: forecast, isLoading } = useForecast({ scenarioId: scenarioId || undefined, sources: active });

  const chartData = useMemo(
    () =>
      (forecast?.series ?? []).map((bucket) => ({
        name: bucket.granularity === 'week' ? bucket.key.replace(/^\d{4}-/, '') : bucket.key,
        label: bucket.label,
        closing: toNumber(bucket.closing),
        ...Object.fromEntries(
          ALL_SOURCES.map((source) => [
            source,
            // Outflows plot below the line, so the two halves read as in and out
            // rather than as one indistinguishable stack.
            (OUTFLOW_SOURCES as readonly string[]).includes(source)
              ? -toNumber(bucket.bySource[source])
              : toNumber(bucket.bySource[source]),
          ]),
        ),
      })),
    [forecast],
  );

  const toggle = (source: FlowSource) =>
    setActive((current) =>
      current.includes(source) ? current.filter((item) => item !== source) : [...current, source],
    );

  return (
    <div>
      <PageHeader
        title="Cash-flow forecast"
        description="Eight weeks ahead in detail, then monthly to twelve months. Every figure comes from a record you can open."
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={scenarioId}
              onChange={(event) => setScenarioId(event.target.value)}
              className="w-44"
            >
              <option value="">Default scenario</option>
              {(scenarios ?? []).map((scenario) => (
                <option key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </option>
              ))}
            </Select>
            <Button variant="outline" onClick={() => setEditing('new')}>
              <Sliders className="me-1.5 h-3.5 w-3.5" />
              Scenarios
            </Button>
          </div>
        }
      />

      {forecast?.shortfall && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="font-medium text-amber-900">
              Cash is forecast to run short in {forecast.shortfall.label}
            </p>
            <p className="text-amber-800">
              The projected closing balance is {formatMoney(forecast.shortfall.closing)} under the
              assumptions currently selected. Switching a source back on, or a different scenario,
              will redraw it.
            </p>
          </div>
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="Cash today" value={forecast?.openingBalance} hint="Across every bank account" />
        <Figure
          label="Money expected in"
          value={sumOf(forecast?.totals, INFLOW_SOURCES, active)}
          hint="Over the whole horizon"
          tone="positive"
        />
        <Figure
          label="Money expected out"
          value={sumOf(forecast?.totals, OUTFLOW_SOURCES, active)}
          hint="Over the whole horizon"
          tone="negative"
        />
        <Figure
          label="Closing balance"
          value={forecast?.closingBalance}
          hint={`In ${forecast?.series.length ?? 0} periods`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Where the money moves</CardTitle>
            <CardDescription>
              Bars are money in and out of each period; the line is the balance carried forward.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <TableSkeleton rows={6} columns={3} />
            ) : (
              <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} stackOffset="sign">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value: number) => compact(value)}
                      width={64}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatMoney(String(Math.abs(value))),
                        SOURCE_LABELS[name as FlowSource] ?? 'Closing balance',
                      ]}
                      labelFormatter={(_label, payload) =>
                        (payload?.[0]?.payload as { label?: string } | undefined)?.label ?? ''
                      }
                    />
                    <Legend
                      formatter={(value: string) => SOURCE_LABELS[value as FlowSource] ?? 'Balance'}
                      wrapperStyle={{ fontSize: 11 }}
                    />
                    <ReferenceLine y={0} stroke="#9ca3af" />
                    {ALL_SOURCES.filter((source) => active.includes(source)).map((source) => (
                      <Bar
                        key={source}
                        dataKey={source}
                        stackId="flow"
                        fill={SOURCE_COLOURS[source]}
                        maxBarSize={28}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="closing"
                      stroke="#111827"
                      strokeWidth={2}
                      dot={false}
                      name="closing"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">What is counted</CardTitle>
            <CardDescription>Untick a source to see the balance without it.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SourceGroup
              title="Money in"
              sources={INFLOW_SOURCES}
              active={active}
              totals={forecast?.totals}
              onToggle={toggle}
            />
            <Separator />
            <SourceGroup
              title="Money out"
              sources={OUTFLOW_SOURCES}
              active={active}
              totals={forecast?.totals}
              onToggle={toggle}
            />
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <PeriodTable forecast={forecast} loading={isLoading} />
        <AccuracyPanel />
      </div>

      {editing && (
        <ScenarioDialog
          scenarios={scenarios ?? []}
          onClose={() => setEditing(null)}
          onPicked={(id) => setScenarioId(id)}
        />
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | null | undefined;
  hint: string;
  tone?: 'positive' | 'negative';
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p
          className={cn(
            'tabular mt-1 text-2xl font-semibold',
            tone === 'positive' && 'text-emerald-700',
            tone === 'negative' && 'text-red-700',
          )}
        >
          {value ? formatMoney(value) : '—'}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function SourceGroup({
  title,
  sources,
  active,
  totals,
  onToggle,
}: {
  title: string;
  sources: readonly FlowSource[];
  active: FlowSource[];
  totals?: Record<string, string | null>;
  onToggle: (source: FlowSource) => void;
}) {
  return (
    <div>
      <p className="eyebrow mb-2 text-[11px] text-muted-foreground">{title}</p>
      <div className="space-y-2.5">
        {sources.map((source) => (
          <label key={source} className="flex cursor-pointer items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={active.includes(source)}
              onCheckedChange={() => onToggle(source)}
            />
            <span
              className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: SOURCE_COLOURS[source] }}
            />
            {/* The amount sits under the name rather than beside it: side by
                side, "Subscriptions & AMC" and a six-figure sum fight for the
                same 150 pixels and the name loses. */}
            <span className="min-w-0 flex-1">
              <span className="block leading-tight">{SOURCE_LABELS[source]}</span>
              <span className="tabular block text-xs text-muted-foreground">
                {totals?.[source] ? formatMoney(totals[source]) : '—'}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function PeriodTable({
  forecast,
  loading,
}: {
  forecast: ReturnType<typeof useForecast>['data'];
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Period by period</CardTitle>
        <CardDescription>Weeks first, because that is where a cash decision gets made.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <TableSkeleton rows={6} columns={4} />
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-end">In</TableHead>
                  <TableHead className="text-end">Out</TableHead>
                  <TableHead className="text-end">Closing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(forecast?.series ?? []).map((bucket) => {
                  const closing = toNumber(bucket.closing);
                  return (
                    <TableRow key={bucket.key}>
                      <TableCell>
                        <span className="text-sm">{bucket.label}</span>
                        {bucket.granularity === 'week' && (
                          <Badge variant="outline" className="ms-2 text-[10px]">
                            week
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="tabular text-end text-emerald-700">
                        {formatMoney(bucket.inflow)}
                      </TableCell>
                      <TableCell className="tabular text-end text-red-700">
                        {formatMoney(bucket.outflow)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'tabular text-end font-medium',
                          closing < 0 && 'text-red-700',
                        )}
                      >
                        {formatMoney(bucket.closing)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AccuracyPanel() {
  const { data } = useForecastAccuracy();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">How good the forecast has been</CardTitle>
        <CardDescription>
          What was projected against what happened. Snapshots are written once and never rewritten.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nothing to compare yet. The first grading happens after a full month has passed.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Scenario</TableHead>
                <TableHead className="text-end">Forecast in</TableHead>
                <TableHead className="text-end">Actual in</TableHead>
                <TableHead className="text-end">Variance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-sm">{row.periodKey}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{row.scenario}</TableCell>
                  <TableCell className="tabular text-end">{formatMoney(row.projectedInflow)}</TableCell>
                  <TableCell className="tabular text-end">{formatMoney(row.actualInflow)}</TableCell>
                  <TableCell className="text-end">
                    {row.variancePercent === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Badge variant={Math.abs(row.variancePercent) <= 10 ? 'secondary' : 'outline'}>
                        {row.variancePercent > 0 ? '+' : ''}
                        {row.variancePercent}%
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ScenarioDialog({
  scenarios,
  onClose,
  onPicked,
}: {
  scenarios: ForecastScenario[];
  onClose: () => void;
  onPicked: (id: string) => void;
}) {
  const [draft, setDraft] = useState<Partial<ForecastScenario>>({
    name: '',
    kind: 'CUSTOM',
    winRatePercent: 100,
    churnPercent: 0,
    collectionDelayDays: 0,
    costInflationPercent: 0,
  });
  const save = useSaveScenario();

  const load = (scenario: ForecastScenario) => setDraft(scenario);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Scenarios</DialogTitle>
          <DialogDescription>
            Best, Expected and Worst are the same maths with different assumptions — which is what
            makes them comparable. Change the numbers, not the model.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 sm:grid-cols-[200px_1fr]">
          <div className="space-y-1">
            {scenarios.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => load(scenario)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors',
                  draft.id === scenario.id ? 'bg-muted font-medium' : 'hover:bg-muted',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{scenario.name}</span>
                {scenario.isDefault && <Badge variant="secondary">Default</Badge>}
              </button>
            ))}
            <button
              onClick={() =>
                setDraft({
                  name: '',
                  kind: 'CUSTOM',
                  winRatePercent: 100,
                  churnPercent: 0,
                  collectionDelayDays: 0,
                  costInflationPercent: 0,
                })
              }
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              New scenario
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="scenario-name">Name</Label>
              <Input
                id="scenario-name"
                value={draft.name ?? ''}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Conservative"
              />
            </div>

            <NumberField
              label="Pipeline that lands"
              suffix="%"
              hint="Applied on top of each deal's own probability."
              value={draft.winRatePercent ?? 100}
              onChange={(value) => setDraft({ ...draft, winRatePercent: value })}
            />
            <NumberField
              label="Monthly churn"
              suffix="%"
              hint="Compounds with distance: a subscription a year out is discounted twelve times."
              value={draft.churnPercent ?? 0}
              onChange={(value) => setDraft({ ...draft, churnPercent: value })}
            />
            <NumberField
              label="Extra collection delay"
              suffix="days"
              hint="On top of how late each client actually pays."
              value={draft.collectionDelayDays ?? 0}
              onChange={(value) => setDraft({ ...draft, collectionDelayDays: value })}
            />
            <NumberField
              label="Cost inflation"
              suffix="%"
              hint="Applied to every modelled outflow."
              value={draft.costInflationPercent ?? 0}
              onChange={(value) => setDraft({ ...draft, costInflationPercent: value })}
            />

            {save.isError && <FormError message={(save.error as ApiRequestError).message} />}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            disabled={!draft.name?.trim() || save.isPending}
            onClick={() =>
              save.mutate(draft as ForecastScenario & { name: string }, {
                onSuccess: (scenario) => {
                  onPicked(scenario.id);
                  onClose();
                },
              })
            }
          >
            {draft.id ? 'Save changes' : 'Create scenario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NumberField({
  label,
  suffix,
  hint,
  value,
  onChange,
}: {
  label: string;
  suffix: string;
  hint: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          className="w-28"
        />
        <span className="text-sm text-muted-foreground">{suffix}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function sumOf(
  totals: Record<string, string | null> | undefined,
  sources: readonly FlowSource[],
  active: FlowSource[],
): string {
  if (!totals) return '0.00';
  const sum = sources
    .filter((source) => active.includes(source))
    .reduce((total, source) => total + Number(totals[source] ?? 0), 0);
  return sum.toFixed(2);
}

/** 1.2M rather than 1200000 on an axis nobody wants to read digit by digit. */
function compact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(value);
}
